// Sugalarm — Android "alarm reliability" bridge wrapper (cycle 303).
//
// The single owner of access to the native `AlarmReliability` plugin. That
// plugin exists ONLY in the Android `standard` (phone) APK (registered in the
// standard MainActivity); it is absent on web, iOS, the Pi kiosk, the Android
// tablet kiosk, and any older `standard` APK that predates it — so
// `isAvailable()` is false on every one of those surfaces and the Settings ->
// Notifications "Alarm reliability" card gates strictly on it.
//
// Like every native plugin in the pure-remote webview (cycle 26/138/197/212),
// it is reached through the bridge-injected `window.Capacitor.Plugins` proxy —
// NEVER a bare `import("@capacitor/...")`. Mirrors `lib/notificationSettings.ts`
// (the iOS analog) and `lib/screenBrightness.ts`.
//
// READ-ONLY state + open-a-system-screen only: sends NO data anywhere, reads
// NO PHI, holds NO secret, makes NO network call. Every call is wrapped and
// no-ops safely (returns null / resolves) off Android-native or on an APK that
// predates the plugin — the crash-safe-sequencing rule (0.198.1/0.203.1).

import { isCapacitorNative, capacitorPlatform, isNativeKioskShell } from "./capacitor";

/** Per-item grant state for the urgent-alarm delivery-reliability settings.
 *  The three cycle-303 keys stay verbatim; cycle 315 added the rest (they
 *  are `undefined` on a phone still running the vc20 APK, and every
 *  consumer falls back gracefully). Cycle 316: on iOS the SAME plugin name
 *  returns the iOS shape — `{ supported: true, platform: "ios",
 *  notificationsEnabled, criticalAlertsAllowed }` — so the Android-only
 *  fields are optional here and the `platform` discriminator selects the
 *  per-platform behavior (older builds omit it; callers fall back to the
 *  Capacitor platform). */
export interface AlarmReliabilityStatus {
  supported: boolean;
  /** Cycle 316: "android" | "ios" (absent on pre-316 builds). */
  platform?: "android" | "ios";
  /** Android 14+ full-screen-intent special access (true below 14). */
  fullScreenIntent: boolean;
  /** App is exempt from battery optimization (immediate doze delivery). */
  batteryOptimizationsIgnored: boolean;
  /** Notification Policy Access — makes the alarm channel's DND bypass real. */
  dndPolicyAccess: boolean;
  /** Cycle 315: the app can post notifications at all (Android 13+ grant).
   *  Cycle 316: also returned by iOS (UNUserNotificationCenter state). */
  notificationsEnabled?: boolean;
  /** Cycle 315: spec-named alias of `dndPolicyAccess`. */
  dndAccess?: boolean;
  /** Cycle 315: spec-named alias of `batteryOptimizationsIgnored`. */
  batteryExempt?: boolean;
  /** Cycle 315: Samsung device — surface the "Sleeping apps" pointer card. */
  isSamsung?: boolean;
  /** Cycle 315: Build.VERSION.SDK_INT — hides the full-screen row below 34. */
  sdkInt?: number;
  /** Cycle 316 (iOS only): Apple's separate Critical Alerts grant. */
  criticalAlertsAllowed?: boolean;
}

/** iOS-only status shape (a narrowed view of the same plugin response). */
export interface IosAlarmReliabilityStatus {
  supported: boolean;
  platform?: "ios";
  notificationsEnabled?: boolean;
  criticalAlertsAllowed?: boolean;
}

interface AlarmReliabilityProxy {
  getStatus(): Promise<AlarmReliabilityStatus>;
  openFullScreenIntentSettings(): Promise<void>;
  openBatteryOptimizationSettings(): Promise<void>;
  openDndPolicySettings(): Promise<void>;
  // Cycle 315 (absent on the vc20 APK — call sites catch + fall back):
  openAppNotificationSettings?(): Promise<void>;
  openDndAccessSettings?(): Promise<void>;
  requestBatteryExemption?(): Promise<void>;
  // Cycle 316 (iOS implementation; absent on Android + older iOS builds):
  requestNotifications?(opts: {
    critical: boolean;
  }): Promise<IosAlarmReliabilityStatus & { granted?: boolean }>;
}

/** True when the SPA runs inside the Android `standard` phone shell (NOT the
 *  device-owner kiosk). The plugin-presence check below is the real gate; this
 *  excludes the kiosk defensively so the card can never render there. */
export function isAndroidPhoneNative(): boolean {
  return (
    isCapacitorNative() &&
    capacitorPlatform() === "android" &&
    !isNativeKioskShell()
  );
}

/** Resolve the natively-registered plugin proxy off the Capacitor bridge, or
 *  null on any surface where it isn't present (everything but the standard
 *  Android APK that carries this build's plugin). */
function plugin(): AlarmReliabilityProxy | null {
  if (!isAndroidPhoneNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.AlarmReliability;
  return p != null ? (p as AlarmReliabilityProxy) : null;
}

/** True iff the native AlarmReliability plugin is registered on the bridge —
 *  i.e. the SPA is inside the Android standard APK with this plugin compiled
 *  in. The "Alarm reliability" card renders only when this is true. */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Read the three grant states, or null off Android-native / on any bridge
 *  error. Never throws. */
export async function getStatus(): Promise<AlarmReliabilityStatus | null> {
  const p = plugin();
  if (p == null) return null;
  try {
    return await p.getStatus();
  } catch (err) {
    console.warn("[alarmReliability] getStatus failed", err);
    return null;
  }
}

/** Open the Android 14+ "full-screen notifications" screen for this app.
 *  No-ops off Android-native / below 14. Never throws. */
export async function openFullScreenIntentSettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.openFullScreenIntentSettings();
  } catch (err) {
    console.warn("[alarmReliability] openFullScreenIntentSettings failed", err);
  }
}

/** Open the battery-optimization list so the user can exempt Sugalarm.
 *  No-ops off Android-native. Never throws. */
export async function openBatteryOptimizationSettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.openBatteryOptimizationSettings();
  } catch (err) {
    console.warn("[alarmReliability] openBatteryOptimizationSettings failed", err);
  }
}

/** Open the Notification Policy Access screen (grants the alarm channel's DND
 *  bypass). No-ops off Android-native. Never throws. */
export async function openDndPolicySettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    await p.openDndPolicySettings();
  } catch (err) {
    console.warn("[alarmReliability] openDndPolicySettings failed", err);
  }
}

// ── Cycle 315 — the Alarm Reliability screen's deep-links ─────────────────
// Each new method falls back to its nearest cycle-303 equivalent when the
// installed APK predates it (vc20), so the Fix buttons always land on a
// usable system screen. Never throws.

/** Open THIS app's notification settings page (master toggle + channels). */
export async function openAppNotificationSettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    if (p.openAppNotificationSettings) {
      await p.openAppNotificationSettings();
    }
  } catch (err) {
    console.warn("[alarmReliability] openAppNotificationSettings failed", err);
  }
}

/** Open the DND-access (Notification Policy Access) list. */
export async function openDndAccessSettings(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    if (p.openDndAccessSettings) {
      await p.openDndAccessSettings();
    } else {
      await p.openDndPolicySettings();
    }
  } catch (err) {
    console.warn("[alarmReliability] openDndAccessSettings failed", err);
  }
}

/** Raise the one-tap battery-exemption request dialog; falls back to the
 *  battery-optimization app list on an older APK. */
export async function requestBatteryExemption(): Promise<void> {
  const p = plugin();
  if (p == null) return;
  try {
    if (p.requestBatteryExemption) {
      await p.requestBatteryExemption();
    } else {
      await p.openBatteryOptimizationSettings();
    }
  } catch (err) {
    console.warn("[alarmReliability] requestBatteryExemption failed", err);
  }
}

// ── Cycle 316 — the iOS surface (the onboarding tour's two iOS steps) ─────
// Deliberately SEPARATE accessors: every cycle-315 export above stays
// Android-gated, so the Android-only surfaces (the Alarm Reliability
// screen, its Settings row, the Now banner) can never light up on iOS now
// that the iOS plugin reports `supported: true`.

/** True when the SPA runs inside the iOS native shell. */
export function isIosPhoneNative(): boolean {
  return isCapacitorNative() && capacitorPlatform() === "ios";
}

/** The plugin proxy on the iOS shell (null elsewhere / on archives that
 *  predate the plugin). */
function iosPlugin(): AlarmReliabilityProxy | null {
  if (!isIosPhoneNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.AlarmReliability;
  return p != null ? (p as AlarmReliabilityProxy) : null;
}

/** iOS grant state, or null off the iOS shell / on an archive without the
 *  cycle-316 implementation (the cycle-315 stub answers
 *  `{ supported: false }`, which callers treat as "can't run the tour").
 *  Never throws. */
export async function getIosStatus(): Promise<IosAlarmReliabilityStatus | null> {
  const p = iosPlugin();
  if (p == null) return null;
  try {
    return (await p.getStatus()) as IosAlarmReliabilityStatus;
  } catch (err) {
    console.warn("[alarmReliability] getIosStatus failed", err);
    return null;
  }
}

/** Fire the standard iOS notification-authorization prompt — including
 *  Apple's separate Critical Alerts prompt when `critical` is true (the
 *  cycle-226 entitlement is approved and in use; this reuses the same
 *  authorization pathway, and never touches APNS registration). Resolves
 *  with the post-request grant state, or null when unavailable (older
 *  archive / off-iOS). Never throws. */
export async function requestIosNotifications(
  critical: boolean,
): Promise<(IosAlarmReliabilityStatus & { granted?: boolean }) | null> {
  const p = iosPlugin();
  if (p == null || typeof p.requestNotifications !== "function") return null;
  try {
    return await p.requestNotifications({ critical });
  } catch (err) {
    console.warn("[alarmReliability] requestNotifications failed", err);
    return null;
  }
}
